/**
 * Чистая логика журналов «Через InstaFarm» и «Вся лента» (02.11.2026).
 */

/** Не больше `limit` запросов разом: страница метрик — это 50 походов в Instagram. */
export async function runLimited<T>(items: readonly T[], limit: number, fn: (item: T) => Promise<void>): Promise<void> {
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const item = items[next++];
      await fn(item).catch(() => undefined);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
}

/** Номер страницы из адреса: мусор и ноль — первая. */
export function pageFromParam(value: string | null): number {
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : 1;
}

/** Разбор комментариев принимает до пяти ссылок — дальше не передаём. */
export const COMMENT_LINKS_MAX = 5;

/** Ссылка на «Комментарии» с уже вставленными ссылками на посты. Только Instagram. */
export function commentsLink(permalinks: readonly (string | null | undefined)[]): string | null {
  const ig = [...new Set(permalinks.filter((p): p is string => !!p && /instagram\.com\//i.test(p)))].slice(0, COMMENT_LINKS_MAX);
  if (!ig.length) return null;
  return `/publications/comments?links=${encodeURIComponent(ig.join('\n'))}`;
}

/** Обратно: ?links= → текст для поля, по ссылке в строке, не больше пяти. */
export function linksFromParam(value: string | null): string {
  if (!value) return '';
  return value.split(/[\n\s,]+/).map((l) => l.trim()).filter(Boolean).slice(0, COMMENT_LINKS_MAX).join('\n');
}

export type JournalFormat = 'all' | 'reel' | 'post';
export type JournalPeriod = 'all' | '7' | '30' | '90';

export interface JournalFilters {
  /** id аккаунта, «group:<id>» — группа пабликов, пусто — все. */
  account: string;
  format: JournalFormat;
  period: JournalPeriod;
}

export const EMPTY_FILTERS: JournalFilters = { account: '', format: 'all', period: 'all' };

export const isFiltered = (f: JournalFilters) => !!f.account || f.format !== 'all' || f.period !== 'all';

/** Фильтр журнала по аккаунту (или группе), формату и периоду. */
export function filterJournal<T extends { accountId: string; type: 'post' | 'reel'; publishedAt: string }>(
  items: readonly T[],
  f: JournalFilters,
  groups: readonly { id: string; accountIds: string[] }[],
  now: number = Date.now(),
): T[] {
  let accountIds: Set<string> | null = null;
  if (f.account.startsWith('group:')) {
    const g = groups.find((x) => x.id === f.account.slice(6));
    accountIds = new Set(g?.accountIds ?? []);
  } else if (f.account) {
    accountIds = new Set([f.account]);
  }
  const since = f.period === 'all' ? null : now - Number(f.period) * 86_400_000;
  return items.filter((p) =>
    (!accountIds || accountIds.has(p.accountId))
    && (f.format === 'all' || p.type === f.format)
    && (since === null || new Date(p.publishedAt).getTime() >= since));
}
